
import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { AlertTriangle } from 'lucide-react';
import { useMatureContent } from '@/hooks/use-mature-content';

interface ImagePreviewProps {
  src: string | null;
  alt: string;
  className?: string;
  type: 'avatar' | 'banner';
  fallback?: React.ReactNode;
  isMature?: boolean;
  showMatureContent?: boolean; // Keep for backward compatibility but prefer unified hook
}

export const ImagePreview = ({
  src,
  alt,
  className,
  type,
  fallback,
  isMature = false,
  showMatureContent: propShowMatureContent
}: ImagePreviewProps) => {
  const [error, setError] = useState(false);
  
  // Use the unified mature content hook
  const { showMatureContent: hookShowMatureContent } = useMatureContent();
  
  // Use prop value if provided, otherwise use hook value
  const showMatureContent = propShowMatureContent !== undefined ? propShowMatureContent : hookShowMatureContent;

  // Determine if we should blur the image
  const shouldBlur = isMature && !showMatureContent;

  if (!src || error) {
    return (
      <div
        className={cn(
          "bg-black/40 flex items-center justify-center",
          type === 'avatar' ? 'rounded-full' : 'rounded-lg',
          className
        )}
      >
        {fallback || (
          <span className="text-gray-400">
            No {type === 'avatar' ? 'avatar' : 'banner'}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <img
        src={src}
        alt={alt}
        className={cn(
          "object-cover object-center transition-all",
          type === 'avatar' ? 'rounded-full' : 'rounded-lg',
          shouldBlur && "blur-xl",
          className
        )}
        onError={() => setError(true)}
      />
      
      {shouldBlur && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-black/70 backdrop-blur-sm p-2 rounded-lg flex items-center gap-2 max-w-[80%] text-center">
            <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0" />
            <span className="text-white text-xs">
              Sensitive content
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
